/**
 * Contacts surface (live) - the filter surface: five-field search
 * (name/company/title/email/source), temperature pills, a gold Follow-up-due
 * pill (8b), source-group pills (only when the live data carries provenance;
 * otherwise the row is omitted as honest absence), the attribute row
 * (Seniority + Function multi-selects with the "inferred from title" caption,
 * Company type-ahead, Title contains), the admin-only owner filter, and the
 * gold-ink truncation notice. Pills reuse the sealed .sx-typepill; the dropdowns
 * are self-contained sx- glass controls with document outside-click close.
 */

import { useState } from "react";
import type { ContactTemperature } from "../types";
import type { ContactFilters, FunctionArea, Seniority } from "../derive";
import { FUNCTION_OPTIONS, SENIORITY_OPTIONS, temperaturePillPatch } from "../derive";
import { useDismiss } from "../use-dismiss";
import { IconChevron, IconClock } from "../contact-icons";

function MultiSelect<T extends string>({
  label,
  caption,
  options,
  selected,
  onChange,
}: {
  label: string;
  caption: string;
  options: readonly T[];
  selected: T[];
  onChange: (next: T[]) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useDismiss<HTMLDivElement>(open, setOpen);
  const count = selected.length;
  const toggle = (o: T) =>
    onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]);

  return (
    <div className="sxc-dd" ref={ref}>
      <button
        type="button"
        className={`sxc-dd-trigger${count ? " sxc-dd-trigger--active" : ""}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span>
          {label}
          {count ? ` (${count})` : ""}
        </span>
        <IconChevron />
      </button>
      {open ? (
        <div className="sxc-dd-menu" role="group" aria-label={label}>
          <p className="sxc-dd-caption">{caption}</p>
          {options.map((o) => (
            <label key={o} className="sxc-check">
              <input
                type="checkbox"
                checked={selected.includes(o)}
                onChange={() => toggle(o)}
              />
              <span>{o}</span>
            </label>
          ))}
          {count > 0 ? (
            <button
              type="button"
              className="sxc-dd-clear"
              onClick={() => onChange([])}
            >
              Clear
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Combobox({
  label,
  value,
  options,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  placeholder: string;
  onChange: (next: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useDismiss<HTMLDivElement>(open, setOpen);
  const filtered = options
    .filter((o) => o.toLowerCase().includes(query.trim().toLowerCase()))
    .slice(0, 60);
  const close = () => {
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="sxc-dd" ref={ref}>
      <button
        type="button"
        className={`sxc-dd-trigger${value ? " sxc-dd-trigger--active" : ""}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{value || label}</span>
        <IconChevron />
      </button>
      {open ? (
        <div className="sxc-dd-menu">
          <input
            className="sxc-dd-search"
            type="search"
            placeholder={placeholder}
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="sxc-dd-list">
            <button
              type="button"
              className="sxc-dd-opt"
              onClick={() => {
                onChange("");
                close();
              }}
            >
              Any {label.toLowerCase()}
            </button>
            {filtered.map((o) => (
              <button
                key={o}
                type="button"
                className={`sxc-dd-opt${o === value ? " sxc-dd-opt--on" : ""}`}
                onClick={() => {
                  onChange(o);
                  close();
                }}
              >
                {o}
              </button>
            ))}
            {filtered.length === 0 ? (
              <p className="sxc-dd-empty">No matches</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const TEMP_PILLS: { key: ContactTemperature | "all"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "hot", label: "Hot" },
  { key: "warm", label: "Warm" },
  { key: "cold", label: "Cold" },
];

export function ContactsFilters({
  filters,
  onChange,
  tempCounts,
  followUpCount,
  activeEngagementCount,
  sourceCounts,
  sourceGroups,
  companies,
  owners,
  showOwnerFilter,
  total,
  shown,
  cap,
}: {
  filters: ContactFilters;
  onChange: (patch: Partial<ContactFilters>) => void;
  tempCounts: Record<ContactTemperature | "all", number>;
  followUpCount: number;
  activeEngagementCount: number;
  sourceCounts: { all: number; byGroup: Map<string, number> };
  sourceGroups: string[];
  companies: string[];
  owners: { id: string; name: string }[];
  showOwnerFilter: boolean;
  total: number;
  shown: number;
  cap: number;
}): JSX.Element {
  const truncated = total > cap;

  return (
    <section className="sx-filters sxc-filters" aria-label="Filters">
      <div className="sxc-search">
        <input
          type="search"
          className="sxc-search-input"
          placeholder="Search contacts by name, company, or title..."
          value={filters.search}
          onChange={(e) => onChange({ search: e.target.value })}
          aria-label="Search contacts"
        />
        {filters.search ? (
          <button
            type="button"
            className="sxc-search-clear"
            aria-label="Clear search"
            onClick={() => onChange({ search: "" })}
          >
            ✕
          </button>
        ) : null}
      </div>

      <div className="sxc-pill-block">
        <p className="sx-f-eyebrow">Temperature</p>
        <div className="sx-pill-row">
          {TEMP_PILLS.map((p) => (
            <button
              key={p.key}
              type="button"
              className="sx-typepill"
              role="radio"
              aria-checked={filters.temperature === p.key}
              onClick={() => onChange(temperaturePillPatch(p.key))}
            >
              {p.label}
              <span className="sx-pct">{tempCounts[p.key]}</span>
            </button>
          ))}
          <button
            type="button"
            className="sx-typepill sxc-followpill"
            aria-pressed={filters.followUpDue}
            onClick={() => onChange({ followUpDue: !filters.followUpDue })}
          >
            <IconClock />
            Follow-up due
            <span className="sx-pct">{followUpCount}</span>
          </button>
          <button
            type="button"
            className="sx-typepill sxc-activepill"
            aria-pressed={filters.activeEngagement}
            onClick={() => onChange({ activeEngagement: !filters.activeEngagement })}
          >
            <span className="sxc-active-led" aria-hidden="true" />
            Active engagement
            <span className="sx-pct">{activeEngagementCount}</span>
          </button>
        </div>
      </div>

      {sourceGroups.length > 0 ? (
        <div className="sxc-pill-block">
          <p className="sx-f-eyebrow">Source group</p>
          <div className="sx-pill-row">
            <button
              type="button"
              className="sx-typepill"
              role="radio"
              aria-checked={filters.sourceGroup === "all"}
              onClick={() => onChange({ sourceGroup: "all" })}
            >
              All
              <span className="sx-pct">{sourceCounts.all}</span>
            </button>
            {sourceGroups.map((g) => (
              <button
                key={g}
                type="button"
                className="sx-typepill"
                role="radio"
                aria-checked={filters.sourceGroup === g}
                onClick={() => onChange({ sourceGroup: g })}
              >
                {g}
                <span className="sx-pct">{sourceCounts.byGroup.get(g) ?? 0}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {showOwnerFilter ? (
        <div className="sxc-pill-block">
          <p className="sx-f-eyebrow">Owner</p>
          <div className="sx-pill-row">
            <button
              type="button"
              className="sx-typepill"
              role="radio"
              aria-checked={filters.ownerId === "all"}
              onClick={() => onChange({ ownerId: "all" })}
            >
              All owners
            </button>
            {owners.map((o) => (
              <button
                key={o.id}
                type="button"
                className="sx-typepill"
                role="radio"
                aria-checked={filters.ownerId === o.id}
                onClick={() => onChange({ ownerId: o.id })}
              >
                {o.name}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="sxc-attrs">
        <MultiSelect<Seniority>
          label="Seniority"
          caption="inferred from title"
          options={SENIORITY_OPTIONS}
          selected={filters.seniority}
          onChange={(next) => onChange({ seniority: next })}
        />
        <MultiSelect<FunctionArea>
          label="Function"
          caption="inferred from title"
          options={FUNCTION_OPTIONS}
          selected={filters.functions}
          onChange={(next) => onChange({ functions: next })}
        />
        <Combobox
          label="Company"
          value={filters.company}
          options={companies}
          placeholder="Search company..."
          onChange={(next) => onChange({ company: next })}
        />
        <input
          type="text"
          className="sxc-titlein"
          placeholder="Title contains..."
          value={filters.titleContains}
          onChange={(e) => onChange({ titleContains: e.target.value })}
          aria-label="Title contains"
        />
      </div>

      {truncated ? (
        <p className="sxc-trunc">
          Showing top {cap.toLocaleString()} of {total.toLocaleString()} contacts
        </p>
      ) : (
        <p className="sxc-count">
          Showing {shown.toLocaleString()} of {total.toLocaleString()} contacts
        </p>
      )}
    </section>
  );
}
